import React from 'react';
import { FileText, ExternalLink, Newspaper, Award, TrendingDown } from '../../../icons';
import type { VideoResult as _VideoResult } from './types';
import { ScoreRing } from './primitives';
import { SentimentSegmentBar } from './charts';

interface NewsArticle {
  snippet?: string;
  source?: string;
  pub_date?: string;
  link?: string;
}

interface ArticleSentiment {
  title?: string;
  sentiment: string;
  category?: string;
  concern_level: string;
}

interface TopicDistItem {
  topic?: string;
  count: number;
  sentiment?: string;
}

interface NewsAnalysis {
  brand_safety_score?: number | null;
  overall_sentiment?: string;
  controversy_level?: string;
  key_topics?: string[];
  summary?: string;
  recommendation?: string;
  topic_distribution?: TopicDistItem[];
  article_sentiments?: ArticleSentiment[];
  positive_signals?: string[];
  risk_signals?: string[];
}

interface NewsData {
  analysis?: NewsAnalysis;
  articles?: NewsArticle[];
  total_articles?: number;
}

export function NewsResultsDashboard({ data }: { data: unknown }) {
  const parsed = data as NewsData;
  const analysis = parsed.analysis;
  const articles: NewsArticle[] = parsed.articles || [];
  const safetyScore: number | null = analysis?.brand_safety_score ?? null;

  const scoreColor = safetyScore != null ? (safetyScore >= 70 ? '#059669' : safetyScore >= 45 ? '#d97706' : '#dc2626') : '#d4d4d4';
  const scoreBg = safetyScore != null ? (safetyScore >= 70 ? '#f0fdf4' : safetyScore >= 45 ? '#fffbeb' : '#fef2f2') : '#fafafa';
  const scoreBorder = safetyScore != null ? (safetyScore >= 70 ? '#bbf7d0' : safetyScore >= 45 ? '#fde68a' : '#fecaca') : '#e5e5e5';
  const scoreLabel = safetyScore != null ? (safetyScore >= 70 ? 'Safe' : safetyScore >= 45 ? 'Moderate' : 'Risky') : null;

  const sentimentColor: Record<string, string> = {
    'Very Positive': '#15803d',
    Positive: '#059669',
    Neutral: '#737373',
    Negative: '#d97706',
    'Very Negative': '#b91c1c',
  };
  const controversyColor: Record<string, string> = {
    None: '#059669',
    Minor: '#737373',
    Moderate: '#d97706',
    High: '#dc2626',
    Severe: '#991b1b',
  };
  const controversyBg: Record<string, string> = {
    None: '#f0fdf4',
    Minor: '#fafafa',
    Moderate: '#fffbeb',
    High: '#fef2f2',
    Severe: '#fef2f2',
  };

  const sentimentBadgeCfg: Record<string, { color: string; bg: string; border: string }> = {
    Positive: { color: '#059669', bg: '#f0fdf4', border: '#bbf7d0' },
    Neutral: { color: '#737373', bg: '#fafafa', border: '#e5e5e5' },
    Negative: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
    Mixed: { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  };
  const concernCfg: Record<string, { color: string; bg: string; border: string }> = {
    Low: { color: '#0369a1', bg: '#eff6ff', border: '#bfdbfe' },
    Medium: { color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    High: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  };

  const topicDist: TopicDistItem[] = (analysis?.topic_distribution || []).slice(0, 7);
  const topicMax = Math.max(1, ...topicDist.map((t) => t.count || 0));

  const articleSentiments: ArticleSentiment[] = analysis?.article_sentiments || [];
  const posCount = articleSentiments.filter(
    (a) => a.sentiment === 'Positive' || a.sentiment === 'Very Positive',
  ).length;
  const neuCount = articleSentiments.filter((a) => a.sentiment === 'Neutral').length;
  const negCount = articleSentiments.filter(
    (a) => a.sentiment === 'Negative' || a.sentiment === 'Very Negative' || a.sentiment === 'Mixed',
  ).length;

  const card: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #E5E5E5',
    borderRadius: 16,

  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 4px 24px' }}>
      {/* ── Hero strip ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: safetyScore != null ? 'auto 1fr 1fr 1fr' : '1fr 1fr 1fr',
          gap: 12,
          alignItems: 'stretch',
        }}
      >
        {/* Score ring — only shown when score is available */}
        {safetyScore != null && (
        <div
          style={{
            ...card,
            background: scoreBg,
            border: `1.5px solid ${scoreBorder}`,
            padding: '20px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            minWidth: 140,
          }}
        >
          <ScoreRing score={safetyScore} size={90} />
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: scoreColor,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {scoreLabel}
            </div>
            <div className="text-caption-medium text-muted-foreground" style={{ marginTop: 2 }}>
              News Safety
            </div>
          </div>
        </div>
        )}

        {/* Overall sentiment */}
        <div style={{ ...card, padding: '20px 20px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#737373',
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 10,
            }}
          >
            Overall Sentiment
          </div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: sentimentColor[analysis?.overall_sentiment ?? ''] || '#737373',
              lineHeight: 1,
              marginBottom: 8,
            }}
          >
            {analysis?.overall_sentiment || '—'}
          </div>
          <div className="text-caption-regular text-muted-foreground" style={{ marginBottom: 12 }}>
            {parsed.total_articles} articles analysed
          </div>
          <SentimentSegmentBar pos={posCount} neu={neuCount} neg={negCount} />
        </div>

        {/* Controversy */}
        <div
          style={{
            ...card,
            padding: '20px 20px',
            background: controversyBg[analysis?.controversy_level ?? ''] || '#fff',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#737373',
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 10,
            }}
          >
            Controversy Level
          </div>
          <div
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: controversyColor[analysis?.controversy_level ?? ''] || '#737373',
              lineHeight: 1,
              marginBottom: 8,
            }}
          >
            {analysis?.controversy_level || '—'}
          </div>
          <div className="text-caption-regular text-muted-foreground" style={{ marginBottom: 14 }}>
            Public risk assessment
          </div>
          {/* Controversy meter */}
          {(() => {
            const levels = ['None', 'Minor', 'Moderate', 'High', 'Severe'];
            const idx = levels.indexOf(analysis?.controversy_level || 'None');
            return (
              <div style={{ display: 'flex', gap: 3 }}>
                {levels.map((l, i) => (
                  <div
                    key={l}
                    style={{
                      flex: 1,
                      height: 4,
                      borderRadius: 2,
                      background: i <= idx ? controversyColor[l] : '#f5f5f5',
                      transition: 'background 0.5s',
                    }}
                  />
                ))}
              </div>
            );
          })()}
        </div>

        {/* Key topics */}
        <div style={{ ...card, padding: '20px 20px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#737373',
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 12,
            }}
          >
            Key Topics
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {(analysis?.key_topics || []).slice(0, 5).map((t: string, i: number) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  className="flex shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground"
                  style={{ width: 18, height: 18 }}
                >
                  {i + 1}
                </span>
                <span className="text-body-sm-medium text-foreground" style={{ lineHeight: 1.4 }}>
                  {t}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Executive Summary ── */}
      {analysis?.summary && (
        <div style={{ ...card, padding: '22px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 7,
                background: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileText size={13} color="#737373" />
            </div>
            <span className="text-body-sm-semibold text-foreground">
              Executive Summary
            </span>
          </div>
          <div
            style={{
              borderLeft: '3px solid #e5e5e5',
              paddingLeft: 16,
              marginBottom: analysis?.recommendation ? 16 : 0,
            }}
          >
            <p className="m-0 text-body-sm-regular text-foreground" style={{ lineHeight: 1.75 }}>
              {analysis?.summary}
            </p>
          </div>
          {analysis?.recommendation && (
            <div
              style={{
                display: 'flex',
                gap: 12,
                padding: '14px 16px',
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid #e5e5e5',
              }}
            >
              <div className="shrink-0 rounded-sm bg-primary" style={{ width: 3 }} />
              <div>
                <div
                  className="text-muted-foreground"
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 4,
                  }}
                >
                  Recommendation
                </div>
                <p className="m-0 text-body-sm-regular text-foreground" style={{ lineHeight: 1.65 }}>
                  {analysis?.recommendation}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Topic distribution (horizontal bars) + Signals ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Topic bars */}
        {topicDist.length > 0 && (
          <div style={{ ...card, padding: '20px 22px' }}>
            <div
              className="text-muted-foreground"
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginBottom: 16,
              }}
            >
              Topic Distribution
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {topicDist.map((t, i) => {
                const pct = Math.round((t.count / topicMax) * 100);
                const isPos = t.sentiment === 'Positive';
                const isNeg = t.sentiment === 'Negative';
                const barColor = isPos ? '#059669' : isNeg ? '#dc2626' : '#737373';
                const barBg = isPos ? '#f0fdf4' : isNeg ? '#fef2f2' : '#fafafa';
                return (
                  <div key={i}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 5,
                      }}
                    >
                      <span className="text-body-sm-medium text-foreground">
                        {t.topic}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: barColor }}>
                          {t.count} articles
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            padding: '1px 6px',
                            borderRadius: 99,
                            background: barBg,
                            color: barColor,
                            border: `1px solid ${barColor}30`,
                            fontWeight: 700,
                          }}
                        >
                          {t.sentiment || 'Mixed'}
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        height: 7,
                        background: '#F5F5F5',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${pct}%`,
                          background: barColor,
                          borderRadius: 4,
                          transition: 'width 0.8s ease',
                          opacity: 0.85,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Signals */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(analysis?.positive_signals?.length ?? 0) > 0 && (
            <div style={{ ...card, padding: '18px 20px', flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    background: '#f0fdf4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #bbf7d0',
                  }}
                >
                  <Award size={13} color="#059669" />
                </div>
                <span className="text-body-sm-semibold text-green-700">
                  Positive Signals
                </span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#059669',
                    background: '#dcfce7',
                    padding: '1px 8px',
                    borderRadius: 99,
                    border: '1px solid #bbf7d0',
                  }}
                >
                  {analysis?.positive_signals?.length}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {(analysis?.positive_signals ?? []).map((s: string, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: '#dcfce7',
                        color: '#059669',
                        fontSize: 10,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      ✓
                    </span>
                    <span className="text-body-sm-regular text-foreground" style={{ lineHeight: 1.55 }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(analysis?.risk_signals?.length ?? 0) > 0 && (
            <div style={{ ...card, padding: '18px 20px', flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    background: '#fef2f2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #fecaca',
                  }}
                >
                  <TrendingDown size={13} color="#dc2626" />
                </div>
                <span className="text-body-sm-semibold text-red-700">
                  Risk Signals
                </span>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#dc2626',
                    background: '#fee2e2',
                    padding: '1px 8px',
                    borderRadius: 99,
                    border: '1px solid #fecaca',
                  }}
                >
                  {analysis?.risk_signals?.length}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {(analysis?.risk_signals ?? []).map((s: string, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span
                      className="flex items-center justify-center rounded-full bg-red-100 text-[11px] font-bold text-red-600"
                      style={{
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      !
                    </span>
                    <span className="text-body-sm-regular text-foreground" style={{ lineHeight: 1.55 }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Article list ── */}
      <div style={{ ...card, overflow: 'hidden' }}>
        <div
          style={{
            padding: '16px 22px',
            borderBottom: '1px solid #f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              className="flex items-center justify-center bg-muted"
              style={{ width: 26, height: 26, borderRadius: 7 }}
            >
              <Newspaper size={13} className="text-muted-foreground" />
            </div>
            <span className="text-body-sm-semibold text-foreground">News Articles</span>
          </div>
          <span
            className="rounded-full bg-muted text-caption-medium text-muted-foreground"
            style={{ padding: '3px 10px' }}
          >
            {parsed.total_articles} found
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {articleSentiments.map((a, i) => {
            const raw = articles[i];
            const _scNeutral = sentimentBadgeCfg['Neutral'] ?? { color: '#737373', bg: '#fafafa', border: '#e5e5e5' };
            const sc = sentimentBadgeCfg[a.sentiment] ?? _scNeutral;
            const cc = concernCfg[a.concern_level];
            return (
              <div
                key={i}
                style={{
                  padding: '16px 22px',
                  borderBottom: i < articleSentiments.length - 1 ? '1px solid #E5E5E5' : 'none',
                  display: 'flex',
                  gap: 16,
                  alignItems: 'flex-start',
                  transition: 'background 0.15s',
                }}
              >
                {/* Left accent */}
                <div
                  style={{
                    width: 3,
                    borderRadius: 2,
                    background: sc.color,
                    alignSelf: 'stretch',
                    flexShrink: 0,
                  }}
                />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    className="text-body-sm-semibold text-foreground"
                    style={{
                      marginBottom: 5,
                      lineHeight: 1.5,
                    }}
                  >
                    {a.title}
                  </div>
                  {raw?.snippet && (
                    <p
                      className="text-body-sm-regular text-muted-foreground"
                      style={{
                        margin: '0 0 10px',
                        lineHeight: 1.6,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
                      }}
                    >
                      {raw.snippet}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {raw?.source && (
                      <span className="flex items-center text-caption-medium text-muted-foreground" style={{ gap: 4 }}>
                        <span
                          style={{
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            background: '#e0dedb',
                            display: 'inline-block',
                          }}
                        />
                        {raw.source}
                      </span>
                    )}
                    {raw?.pub_date && (
                      <span className="text-caption-regular text-muted-foreground">
                        {raw.pub_date.replace(/ \+\d+ GMT/, '').replace(/ \d{4}$/, '')}
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 9px',
                        borderRadius: 99,
                        fontWeight: 700,
                        color: sc.color,
                        background: sc.bg,
                        border: `1px solid ${sc.border}`,
                      }}
                    >
                      {a.sentiment}
                    </span>
                    {a.category && (
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 9px',
                          borderRadius: 4,
                          fontWeight: 600,
                          color: '#737373',
                          background: '#f5f5f5',
                          border: '1px solid #e5e5e5',
                        }}
                      >
                        {a.category}
                      </span>
                    )}
                    {cc && a.concern_level !== 'None' && (
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 9px',
                          borderRadius: 99,
                          fontWeight: 700,
                          color: cc.color,
                          background: cc.bg,
                          border: `1px solid ${cc.border}`,
                        }}
                      >
                        {a.concern_level} concern
                      </span>
                    )}
                  </div>
                </div>

                {raw?.link && (
                  <a
                    href={raw.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: 9,
                      background: '#fafafa',
                      border: '1px solid #e5e5e5',
                      color: '#737373',
                      textDecoration: 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    <ExternalLink size={13} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
